import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { DISCLAIMER_TEXT_QUERY_KEY_PREFIX } from 'components/acquisitions/constants/api';
import { CASL_DISCLOSURE } from 'components/admin/utils/feature-flags';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useInvalidateOnboardingState } from 'providers/ProgressiveOnboarding/queries';
import { toastError } from '../billing/common/utils';
import {
  getCaslConfig,
  getComplianceContactInfo,
  getFinancialIncentiveConfig,
  getLocalizedDisclaimerLinks,
  initComplianceContactInfo,
  setCaslConfig,
  setComplianceContactInfo,
  setFinancialIncentiveConfig,
  setLocalizedDisclaimerLinks,
} from './complianceClient';
import {
  CaslConfig,
  ComplianceContactInfo,
  FinancialIncentiveConfig,
  FinancialIncentiveConfigInput,
  LocalizedDisclaimerLinks,
  LocalizedDisclaimerLinksInput,
} from './types';

const CASL_CONFIG = 'casl_config';
const COMPLIANCE_CONTACT_INFO = 'compliance_contact_info';
const FINANCIAL_INCENTIVE_CONFIG = 'financial_incentive_config';
const LOCALIZED_DISCLAIMER_LINKS = 'localized_disclaimer_links';

const staleTime = 1000 * 60;

export function useGetComplianceContactInfo(
  options?: UseQueryOptions<
    ComplianceContactInfo,
    any,
    ComplianceContactInfo,
    [typeof COMPLIANCE_CONTACT_INFO]
  >,
): UseQueryResult<ComplianceContactInfo> {
  return useQuery({
    queryKey: [COMPLIANCE_CONTACT_INFO],
    queryFn: getComplianceContactInfo,
    onError: toastError, // TODO: useQuery callbacks removed in v5
    staleTime,
    ...options,
  });
}

export function useInitComplianceContactInfo(): UseMutationResult<
  ComplianceContactInfo,
  unknown,
  void
> {
  const queryClient = useQueryClient();

  return useMutation(initComplianceContactInfo, {
    onSuccess: (data) => {
      queryClient.setQueryData([COMPLIANCE_CONTACT_INFO], data);
    },
    onError: toastError,
  });
}

export function useSetComplianceContactInfo(): UseMutationResult<
  ComplianceContactInfo,
  unknown,
  ComplianceContactInfo
> {
  const queryClient = useQueryClient();
  const invalidateOnboardingState = useInvalidateOnboardingState();

  return useMutation(setComplianceContactInfo, {
    onSuccess: () => {
      queryClient.refetchQueries([COMPLIANCE_CONTACT_INFO]);
      invalidateOnboardingState();
    },
    onError: toastError,
  });
}

export function useGetFinancialIncentiveConfig(
  options?: UseQueryOptions<
    FinancialIncentiveConfig,
    any,
    FinancialIncentiveConfig,
    [typeof FINANCIAL_INCENTIVE_CONFIG]
  >,
): UseQueryResult<FinancialIncentiveConfig> {
  return useQuery({
    queryKey: [FINANCIAL_INCENTIVE_CONFIG],
    queryFn: getFinancialIncentiveConfig,
    onError: toastError,
    staleTime,
    ...options,
  });
}

export function useSetFinancialIncentiveConfig(): UseMutationResult<
  FinancialIncentiveConfig,
  unknown,
  FinancialIncentiveConfigInput
> {
  const queryClient = useQueryClient();

  return useMutation(setFinancialIncentiveConfig, {
    onSuccess: (data) => {
      queryClient.setQueryData([FINANCIAL_INCENTIVE_CONFIG], data);
    },
  });
}

export function useGetLocalizedDisclaimerLinks(
  options?: UseQueryOptions<
    LocalizedDisclaimerLinks,
    unknown,
    LocalizedDisclaimerLinks,
    [typeof LOCALIZED_DISCLAIMER_LINKS]
  >,
): UseQueryResult<LocalizedDisclaimerLinks> {
  return useQuery({
    queryKey: [LOCALIZED_DISCLAIMER_LINKS],
    queryFn: getLocalizedDisclaimerLinks,
    onError: toastError,
    staleTime,
    ...options,
  });
}

export function useSetLocalizedDisclaimerLinks(): UseMutationResult<
  LocalizedDisclaimerLinks,
  unknown,
  LocalizedDisclaimerLinksInput
> {
  const queryClient = useQueryClient();

  return useMutation(setLocalizedDisclaimerLinks, {
    onSuccess: (data) => {
      queryClient.setQueryData([LOCALIZED_DISCLAIMER_LINKS], data);
    },
  });
}

export function useGetCaslConfig(
  options?: UseQueryOptions<
    CaslConfig,
    unknown,
    CaslConfig,
    [typeof CASL_CONFIG]
  >,
): UseQueryResult<CaslConfig> {
  return useQuery({
    queryKey: [CASL_CONFIG],
    queryFn: getCaslConfig,
    onError: toastError,
    staleTime,
    ...options,
  });
}

export function useSetCaslConfig(): UseMutationResult<
  CaslConfig,
  unknown,
  CaslConfig
> {
  const queryClient = useQueryClient();

  return useMutation(setCaslConfig, {
    onSuccess: async (data) => {
      // An in-flight GET resolving after the save would clobber the saved config.
      await queryClient.cancelQueries([CASL_CONFIG]);
      queryClient.setQueryData([CASL_CONFIG], data);
      // Same race for an in-flight (even inactive) disclaimer preview.
      await queryClient.cancelQueries([DISCLAIMER_TEXT_QUERY_KEY_PREFIX]);
      queryClient.invalidateQueries([DISCLAIMER_TEXT_QUERY_KEY_PREFIX]);
    },
  });
}

/** `undefined` while the config is loading or its latest fetch failed (unknown). */
export function useIsCaslEnabled(): boolean | undefined {
  const flagOn = usePSLabs().hasLabsFlag(CASL_DISCLOSURE);
  const { data, isError } = useGetCaslConfig({
    enabled: flagOn,
    onError: () => undefined,
  });
  if (!flagOn) return false;
  // A failed refetch keeps the previous data; don't treat it as current.
  return isError ? undefined : data?.enabled;
}
